import { useMemo, useState } from "react";
import { Field, Select, TextInput, Callout, FkPrimary } from "../../../product_config/formkit";
import { getMiniform } from "../miniforms.js";
import { buildSimulacionPayload } from "./buildPayload.js";
import { simularCalculo, resumenError } from "../../../../services/calculatorSimService";
import comunas from "../../../../lib/comunas.json";

const fmtUF = (v) => new Intl.NumberFormat("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v ?? 0);
const fmtCLP = (v) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(v ?? 0);

const subTitle = { fontSize: 13, fontWeight: 700, letterSpacing: "-0.01em", margin: "0 0 8px" };

// Simula la calculadora configurada de dos formas:
//  1) MiniForm real: embebe por iframe el MISMO ProductMiniForm de flow
//     (/embed/miniform/[producto_id]) → simulación y respuesta idénticas a producción.
//  2) Prueba rápida por plan: replica el payload del miniform y pega directo al
//     microservicio calculator por planId (útil para probar una fila de Precios Base puntual).
export default function SimuladorSection({ planId, productoId, config, preciosBase, hasChanges }) {
  const miniform = config?.miniform || "";
  const def = getMiniform(miniform);
  const flowUrl = (process.env.REACT_APP_FLOW_URL || "").replace(/\/+$/, "");
  const [inputs, setInputs] = useState({ numCargas: "0", numeroMascotas: "1", cantidad: "1", tipoConstruccion: "Solida" });
  const [estado, setEstado] = useState({ loading: false, resultado: null, error: null });

  // rc_medica: los valores válidos de especialidad viven en precios_base.atributos
  const especialidades = useMemo(() => {
    const set = new Set();
    (preciosBase || []).forEach((row) => {
      const e = row?.atributos?.especialidad;
      if (e !== undefined && e !== null && e !== "") set.add(String(e));
    });
    return [...set].sort();
  }, [preciosBase]);

  const setInput = (k, v) => setInputs((prev) => ({ ...prev, [k]: v }));

  if (!planId) {
    return (
      <Callout tone="info" title="Plan sin guardar">
        Guarda el plan primero: el simulador prueba la configuración guardada en la base de datos.
      </Callout>
    );
  }
  if (!def) {
    return (
      <Callout tone="info" title="Sin miniform asignado">
        Asigna un miniform (sección anterior) para habilitar el simulador.
      </Callout>
    );
  }

  const simular = async () => {
    let payload;
    try {
      // p × q: el nombre del atributo y el tope son del plan, no del formulario.
      const inputsConConfig = miniform === "precio_cantidad"
        ? { ...inputs, cantidadAtributo: config?.cantidad?.atributo, cantidadMaximo: config?.cantidad?.maximo }
        : inputs;
      payload = buildSimulacionPayload(miniform, planId, inputsConConfig);
    } catch (err) {
      setEstado({ loading: false, resultado: null, error: err.message });
      return;
    }
    setEstado({ loading: true, resultado: null, error: null });
    const { status, data } = await simularCalculo(payload);
    if (status === 200) setEstado({ loading: false, resultado: data, error: null });
    else setEstado({ loading: false, resultado: null, error: resumenError(status, data) });
  };

  const r = estado.resultado;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {hasChanges && (
        <Callout tone="warn" title="Cambios sin guardar">
          El simulador usa la configuración guardada en la base de datos. Guarda el plan para probar los cambios recientes.
        </Callout>
      )}

      {/* 1) MiniForm real embebido desde flow (reutiliza el componente de producción) */}
      <div>
        <p style={subTitle}>MiniForm real (flow)</p>
        {!flowUrl ? (
          <Callout tone="info" title="Falta configurar flow">
            Define <code>REACT_APP_FLOW_URL</code> (ej. http://localhost:3000) para embeber el MiniForm real de flow.
          </Callout>
        ) : !productoId ? (
          <Callout tone="info" title="Plan sin producto">
            Asocia el plan a un producto para cargar su MiniForm en flow.
          </Callout>
        ) : (
          <iframe
            title={`MiniForm ${def.label}`}
            src={`${flowUrl}/embed/miniform/${productoId}`}
            style={{ width: "100%", height: 760, border: "1px solid #E5E7EB", borderRadius: 12 }}
          />
        )}
      </div>

      {/* 2) Prueba rápida por plan (payload directo al microservicio calculator) */}
      <div>
        <p style={subTitle}>Prueba rápida por plan (payload directo)</p>
        {!def.usaCalculator ? (
          <Callout tone="warn" title="No disponible para este miniform">
            {def.label} cotiza vía REALLE (tadmin /api/hogar/cotizar) y no usa el microservicio calculator. Usa el MiniForm real de arriba.
          </Callout>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {miniform === "complementario" && (
              <>
                <Field label="Fecha de nacimiento del titular" htmlFor="sim-fnac">
                  <TextInput id="sim-fnac" type="date" value={inputs.fechaNacimiento || ""} onChange={(e) => setInput("fechaNacimiento", e.target.value)} />
                </Field>
                <Field label="N° de cargas" hint="Se simulan como hijos (fonasa) para ejercitar el modelo de cargas del plan." htmlFor="sim-cargas">
                  <TextInput id="sim-cargas" type="number" value={inputs.numCargas} onChange={(e) => setInput("numCargas", e.target.value)} />
                </Field>
              </>
            )}

            {miniform === "incendio" && (
              <>
                <Field label="Tipo de construcción" htmlFor="sim-construccion">
                  <Select
                    id="sim-construccion"
                    value={inputs.tipoConstruccion}
                    options={[{ value: "Solida", label: "Sólida" }, { value: "Mixta", label: "Mixta" }]}
                    onChange={(e) => setInput("tipoConstruccion", e.target.value)}
                  />
                </Field>
                <Field label="Comuna" hint="El código CUT viaja como atributos.comunas." htmlFor="sim-comuna">
                  <Select
                    id="sim-comuna"
                    value={inputs.comunaCodigo || ""}
                    placeholder="Seleccionar comuna"
                    options={comunas.map((c) => ({ value: c.codigo, label: `${c.nombre} (${c.provincia})` }))}
                    onChange={(e) => setInput("comunaCodigo", e.target.value)}
                  />
                </Field>
              </>
            )}

            {miniform === "pet" && (
              <Field label="N° de mascotas" hint="Entre 1 y 5, igual que el MiniForm Pet de flow." htmlFor="sim-mascotas">
                <TextInput id="sim-mascotas" type="number" value={inputs.numeroMascotas} onChange={(e) => setInput("numeroMascotas", e.target.value)} />
              </Field>
            )}

            {miniform === "precio_cantidad" && (
              <Field
                label="Cantidad"
                hint={`Viaja como atributos.${config?.cantidad?.atributo || "cantidad"}${
                  config?.cantidad?.maximo ? `; el máximo del plan es ${config.cantidad.maximo}` : ""
                }. Prima = precio unitario × cantidad.`}
                htmlFor="sim-cantidad"
              >
                <TextInput id="sim-cantidad" type="number" min="1" value={inputs.cantidad} onChange={(e) => setInput("cantidad", e.target.value)} />
              </Field>
            )}

            {miniform === "monto_tasa" && (
              <Field
                label="Monto asegurado (UF)"
                hint="El cliente lo ingresa al cotizar (montoAsegurado.origen: request). Prima = monto × tasa del plan."
                htmlFor="sim-monto"
              >
                <TextInput id="sim-monto" type="number" value={inputs.montoAsegurado || ""} onChange={(e) => setInput("montoAsegurado", e.target.value)} placeholder="ej: 40" />
              </Field>
            )}

            {miniform === "rc_medica" && (
              <Field
                label="Especialidad"
                hint={especialidades.length > 0
                  ? "Valores encontrados en Precios Base (atributos.especialidad)."
                  : "Este plan no tiene atributos.especialidad en Precios Base; escribe el código a probar."}
                htmlFor="sim-especialidad"
              >
                {especialidades.length > 0 ? (
                  <Select
                    id="sim-especialidad"
                    value={inputs.especialidad || ""}
                    placeholder="Seleccionar especialidad"
                    options={especialidades.map((e) => ({ value: e, label: e }))}
                    onChange={(e) => setInput("especialidad", e.target.value)}
                  />
                ) : (
                  <TextInput id="sim-especialidad" value={inputs.especialidad || ""} onChange={(e) => setInput("especialidad", e.target.value)} placeholder="ej: 28" />
                )}
              </Field>
            )}

            <div>
              <FkPrimary onClick={simular} disabled={estado.loading}>
                {estado.loading ? "Calculando..." : "Calcular prima"}
              </FkPrimary>
            </div>

            {estado.error && (
              <Callout tone="warn" title="Error de simulación">{estado.error}</Callout>
            )}

            {r && (
              <Callout tone="success" title={`Prima total: UF ${fmtUF(r?.total?.primaUF)} (≈ ${fmtCLP(r?.total?.primaPesos)})`}>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <span>Titular: UF {fmtUF(r?.titular?.primaUF)}</span>
                  {(r?.resumen?.numeroCargas ?? 0) > 0 && (
                    <span>
                      Cargas ({r.resumen.numeroCargas}): UF {fmtUF(r?.cargasFamiliares?.totalUF)} (≈ {fmtCLP(r?.cargasFamiliares?.totalPesos)})
                      {r?.cargasFamiliares?.descuentoAplicado ? ` · descuento ${r.cargasFamiliares.descuentoAplicado}%` : ""}
                    </span>
                  )}
                  {r?.total?.precioBaseId && <span>precio_base_id: {r.total.precioBaseId}</span>}
                  {r?.resumen?.fechaCalculo && <span>Calculado: {new Date(r.resumen.fechaCalculo).toLocaleString("es-CL")}</span>}
                </div>
              </Callout>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
